import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { sharedConfig } from 'src/environments/shared-config';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { UserData } from '../auth-service/user-data.model';
import { SupportedLocale } from './locale.model';
import {
  buildAcceptLanguageHeader,
  languageSubtag,
  matchSupportedLocale,
  normalizeCultureTag,
  resolveApiCultureCandidate,
  resolveDefaultCulture,
  resolveEffectiveLocales,
  resolvePreferredCultureCandidate,
  usesGBWeightUnits,
  usesUSDateFormat,
} from './locale.util';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private currentCulture: string;
  private currentDir: 'ltr' | 'rtl' = 'ltr';
  private initialized = false;

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    const allowlist = this.getAvailableLocales();
    const defaultCulture = this.getDefaultCulture();
    const matched = matchSupportedLocale(defaultCulture, allowlist, defaultCulture);
    this.currentCulture = matched.culture;
    this.currentDir = matched.dir;
  }

  getAvailableLocales(): SupportedLocale[] {
    const envLocales = (environment as { availablelanguages?: SupportedLocale[] }).availablelanguages;
    return resolveEffectiveLocales(envLocales, sharedConfig.availablelanguages);
  }

  getDefaultCulture(): string {
    const envDefault = (environment as { defaultCulture?: string }).defaultCulture;
    return resolveDefaultCulture(envDefault, sharedConfig.defaultCulture, this.getAvailableLocales());
  }

  getCulture(): string {
    return this.currentCulture;
  }

  /**
   * Language subtag for native SDKs that still expect short codes (Anura, Vibe, Android values-*).
   * ngx-translate packs use {@link getCulture} (e.g. en-ZA).
   */
  getUiLanguage(): string {
    return languageSubtag(this.currentCulture) || 'en';
  }

  getIsRTL(): boolean {
    return this.currentDir === 'rtl';
  }

  /** True when the active culture is US English (for date order / formatting). */
  isUSCulture(): boolean {
    return usesUSDateFormat(this.currentCulture);
  }

  /** True when the active culture is English (United Kingdom). */
  isGBCulture(): boolean {
    return usesGBWeightUnits(this.currentCulture);
  }

  /** Header value for .NET RequestLocalization / Accept-Language. */
  getAcceptLanguageHeader(): string {
    return buildAcceptLanguageHeader(this.currentCulture);
  }

  /** Saved profile.languageCode from local cache (authoritative for API Accept-Language). */
  async getCachedProfileLanguageCode(): Promise<string | null> {
    try {
      const raw = await SecureStorageService.get(SecureStorageKey.MyProfile);
      if (!raw) {
        return null;
      }
      const profile = JSON.parse(raw) as { languageCode?: string | null };
      const culture = normalizeCultureTag(profile.languageCode);
      return culture || null;
    } catch {
      return null;
    }
  }

  /**
   * Resolve preferred culture from saved profile → storage → token → device → default, then apply.
   */
  async init(tokenLanguage?: string | null): Promise<SupportedLocale> {
    const allowlist = this.getAvailableLocales();
    const defaultCulture = this.getDefaultCulture();

    const profileLanguage = await this.getCachedProfileLanguageCode();
    const stored = await SecureStorageService.get(SecureStorageKey.PreferredCulture);

    let candidate = resolveApiCultureCandidate(
      profileLanguage,
      stored,
      tokenLanguage,
      allowlist,
      defaultCulture,
    );

    if (!normalizeCultureTag(profileLanguage) && !normalizeCultureTag(stored) && !normalizeCultureTag(tokenLanguage)) {
      candidate = matchSupportedLocale(await this.probeDeviceCulture(), allowlist, defaultCulture).culture;
    }

    const matched = matchSupportedLocale(candidate, allowlist, defaultCulture);
    await this.applyLocale(matched, true);
    this.initialized = true;
    return matched;
  }

  /**
   * Anonymous public pages (help centre landing, etc.):
   * stored preference → device/browser → flavour default.
   * Ignores profile, token, and in-memory current culture so first visit follows the device.
   */
  async resolveAnonymousPreferredCulture(): Promise<string> {
    const allowlist = this.getAvailableLocales();
    const defaultCulture = this.getDefaultCulture();
    const stored = normalizeCultureTag(await SecureStorageService.get(SecureStorageKey.PreferredCulture));
    if (stored) {
      return matchSupportedLocale(stored, allowlist, defaultCulture).culture;
    }
    const device = await this.probeDeviceCulture();
    return matchSupportedLocale(device, allowlist, defaultCulture).culture;
  }

  /**
   * Resolve a registration/profile culture against the flavour allowlist.
   * Order: explicit candidate → current culture (if init ran) → device/browser → defaultCulture.
   * Does not apply the locale; callers decide whether to setCulture / seed RegisterModel.
   */
  async resolvePreferredCulture(candidate?: string | null): Promise<string> {
    const allowlist = this.getAvailableLocales();
    const defaultCulture = this.getDefaultCulture();
    const device = await this.probeDeviceCulture();
    return resolvePreferredCultureCandidate(
      candidate,
      this.initialized ? this.currentCulture : null,
      device,
      allowlist,
      defaultCulture,
    );
  }

  /**
   * Re-apply from token/profile language after login (legacy bare "en" still matches).
   */
  async applyFromProfileLanguage(language: string | null | undefined): Promise<SupportedLocale> {
    const allowlist = this.getAvailableLocales();
    const matched = matchSupportedLocale(language, allowlist, this.getDefaultCulture());
    await this.applyLocale(matched, true);
    return matched;
  }

  /**
   * Apply a user-selected culture (picker value). Persists preference and optional UserData.language.
   */
  async setCulture(cultureOrCode: string, options?: { persistUserData?: boolean; fireTranslateEvents?: boolean }): Promise<SupportedLocale> {
    const allowlist = this.getAvailableLocales();
    const matched = matchSupportedLocale(cultureOrCode, allowlist, this.getDefaultCulture());
    await this.applyLocale(matched, options?.fireTranslateEvents !== false);
    if (options?.persistUserData !== false) {
      await this.syncUserDataLanguage(matched.culture);
    }
    return matched;
  }

  private async applyLocale(locale: SupportedLocale, fireTranslateEvents: boolean): Promise<void> {
    this.currentCulture = locale.culture;
    this.currentDir = locale.dir;

    this.document.documentElement.lang = locale.culture;
    this.document.documentElement.dir = locale.dir;
    const elem = this.document.getElementsByClassName('section-container')[0] as HTMLElement | undefined;
    if (elem) {
      elem.dir = locale.dir;
    }

    const fallback =
      this.getDefaultCulture() ||
      sharedConfig.defaultCulture ||
      'en-ZA';

    if (!this.translate.getFallbackLang()) {
      this.translate.setFallbackLang(fallback);
    }

    if (fireTranslateEvents) {
      await lastValueFrom(this.translate.use(locale.culture));
    }

    await SecureStorageService.set(SecureStorageKey.PreferredCulture, locale.culture);
  }

  private async probeDeviceCulture(): Promise<string> {
    try {
      if (Capacitor.isNativePlatform()) {
        const tag = await Device.getLanguageTag();
        return normalizeCultureTag(tag?.value);
      }
    } catch {
      // fall through to navigator
    }
    return normalizeCultureTag(
      (typeof navigator !== 'undefined' && (navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage)) || '',
    );
  }

  private async syncUserDataLanguage(culture: string): Promise<void> {
    try {
      const raw = await SecureStorageService.get(SecureStorageKey.CurrentUser);
      if (!raw) {
        return;
      }
      const user = JSON.parse(raw) as UserData;
      if (user.language === culture) {
        return;
      }
      user.language = culture;
      await SecureStorageService.set(SecureStorageKey.CurrentUser, JSON.stringify(user));
    } catch {
      // ignore storage sync failures
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
